import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Company logo path
const COMPANY_LOGO = '/logo-aretina.png';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('¡Bienvenido!');
      navigate('/inicio');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-slate-900"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
        <div className="relative z-10 w-full flex flex-col items-center justify-center p-12">
          {/* Logo on left side */}
          {!logoError ? (
            <img
              src={COMPANY_LOGO}
              alt="Aretina"
              className="h-32 w-auto object-contain mb-8"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="mb-8">
              <div className="w-24 h-24 bg-red-600 rounded-2xl flex items-center justify-center">
                <span className="text-white text-4xl font-bold font-['Manrope']">A</span>
              </div>
            </div>
          )}
          <p className="text-slate-300 text-lg text-center max-w-md">
            Plataforma integral de gestión empresarial
          </p>
          <p className="text-slate-500 text-sm text-center mt-2">
            Indicadores, análisis y soporte centralizado
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            {!logoError ? (
              <img
                src={COMPANY_LOGO}
                alt="Aretina"
                className="h-20 w-auto object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-2xl font-bold font-['Manrope']">A</span>
                </div>
                <h1 className="text-red-600 font-bold text-2xl font-['Manrope']">aretina</h1>
              </div>
            )}
          </div>

          <Card className="border-0 shadow-xl" data-testid="login-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900">
                Iniciar sesión
              </CardTitle>
              <CardDescription className="text-slate-500">
                Ingresa tus credenciales para acceder
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Correo electrónico / Usuario</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="ejemplo@email.com / usuario"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Ingresando...' : 'Ingresar'}
                </Button>
              </form>

              <p className="mt-6 text-center text-slate-400 text-xs">
                Sistema de uso interno. Contacte al administrador para obtener acceso.
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-slate-400 text-xs mt-6">
            © {new Date().getFullYear()} Aretina. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
